import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

export interface WpConnection {
  id: string
  name: string
  token_hint: string
  token_expires_at: string
  site_url: string | null
  site_name: string | null
  connected_at: string | null
  last_sync_at: string | null
  created_at: string
  status: 'pending' | 'connected' | 'expired'
}

export interface WpArticle {
  id: string
  connection_id: string
  wp_post_id: number
  title: string
  excerpt: string | null
  cover_image_url: string | null
  author: string | null
  permalink: string | null
  published_at: string | null
  status: 'draft' | 'published'
  categories: string[]
  tags: string[]
  synced_at: string
}

export interface WpArticleGroup {
  id: string
  name: string
  article_count: number
  created_at: string
  updated_at: string
}

export interface WpArticleGroupDetail {
  id: string
  name: string
  created_at: string
  updated_at: string
  articles: WpArticle[]
}

interface CreateTokenRequest {
  name: string
  ttl: '1h' | '24h' | '7d' | '30d' | 'never'
}

interface CreateTokenResponse {
  id: string
  name: string
  token: string
  token_hint: string
  expires_at: string
  warning: string
}

const CONNECTIONS_KEY = ['wp-connections'] as const
const ARTICLES_KEY = ['wp-articles'] as const
const GROUPS_KEY = ['wp-groups'] as const

export function useWpConnections(): ReturnType<typeof useQuery<{ data: WpConnection[] }>> {
  return useQuery({
    queryKey: CONNECTIONS_KEY,
    queryFn: () => apiClient.get<{ data: WpConnection[] }>(endpoints.wordpress.connections),
  })
}

export function useCreateWpToken(): ReturnType<
  typeof useMutation<CreateTokenResponse, unknown, CreateTokenRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTokenRequest) =>
      apiClient.post<CreateTokenResponse>(endpoints.wordpress.createToken, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  })
}

export function useRevokeWpConnection(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.wordpress.revokeConnection(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONNECTIONS_KEY })
      qc.invalidateQueries({ queryKey: ARTICLES_KEY })
    },
  })
}

export function useWpArticles(params?: {
  page?: number
  per_page?: number
}): ReturnType<typeof useQuery<{ data: WpArticle[]; page: number; per_page: number }>> {
  return useQuery({
    queryKey: [...ARTICLES_KEY, params],
    queryFn: () =>
      apiClient.get<{ data: WpArticle[]; page: number; per_page: number }>(
        endpoints.wordpress.articles(params),
      ),
  })
}

export function useDeleteWpArticle(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.wordpress.deleteArticle(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ARTICLES_KEY }),
  })
}

// --- Article Groups ---

export function useWpGroups(): ReturnType<typeof useQuery<{ data: WpArticleGroup[] }>> {
  return useQuery({
    queryKey: GROUPS_KEY,
    queryFn: () => apiClient.get<{ data: WpArticleGroup[] }>(endpoints.wordpress.groups),
  })
}

export function useWpGroupDetail(id: string): ReturnType<typeof useQuery<WpArticleGroupDetail>> {
  return useQuery({
    queryKey: [...GROUPS_KEY, id],
    queryFn: () => apiClient.get<WpArticleGroupDetail>(endpoints.wordpress.groupDetail(id)),
    enabled: !!id,
  })
}

interface CreateGroupRequest {
  name: string
  article_ids: string[]
}

export function useCreateWpGroup(): ReturnType<
  typeof useMutation<{ id: string }, unknown, CreateGroupRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateGroupRequest) =>
      apiClient.post<{ id: string }>(endpoints.wordpress.createGroup, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
  })
}

export function useDeleteWpGroup(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.wordpress.deleteGroup(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
  })
}
